import React, { useMemo } from 'react';
import { Filter, OperatorType, RadioButton } from '@fabric-ui/components';
import { Box, Divider } from '@mui/material';
import {
  FieldFilterMode,
  FieldFilterModeType,
  getFieldTypeInfo,
  isAdvancedFilterMode,
  isConditionFilterMode,
  isListFilterMode,
} from '../../../utils/filterUtils';
import { useFilterConfig } from '../FilterContext';
import { AdvancedFilterValue, ConditionOperand, FieldFilterValue, FilterOption } from '../types';
import FilterActionButtons from './FilterActionButtons';
import FilterByAdvanced from './FilterByAdvanced';
import { FilterByCondition } from './FilterByCondition';
import { FilterByList } from './FilterByList';

interface FieldFilterTabsProps {
  field: string;
  type: string;
  mode: FieldFilterModeType;
  setMode: (mode: FieldFilterModeType) => void;
  isSchemaField: boolean;
  initialFilter: ConditionOperand;
  label: React.ReactNode;
  // List props
  isLoadingOptions: boolean;
  calcOptions: FilterOption[];
  value: unknown[];
  totalRowCount: number | undefined;
  emptyState?: React.ReactNode;
  setSearch: (search: string) => void;
  onChange: (field: string, value: unknown) => void;
  onFilterChange?: (filter: Filter) => void;
  filterObject: FieldFilterValue | undefined;
  // Condition props
  conditionValue: FieldFilterValue;
  operator: OperatorType;
  isMultipleCondition: boolean;
  disablePortalInOperatorSelect: boolean;
  handleSelectConditionChange: (value: unknown, index: number) => void;
  setErrors: React.Dispatch<React.SetStateAction<ConditionOperand[]>>;
  setConditionValue: React.Dispatch<React.SetStateAction<FieldFilterValue>>;
  errors: ConditionOperand[];
  // Advanced props
  activeProjName: string | undefined;
  advancedFilterValue: AdvancedFilterValue;
  handleAdvancedFilterChange: (value: AdvancedFilterValue) => void;
  // Action buttons props
  setOpen: (open: boolean) => void;
  getErrors: (val: unknown, operator: string | undefined) => string[] | undefined;
  setAdvancedFilterValue: React.Dispatch<React.SetStateAction<AdvancedFilterValue>>;
  open?: boolean;
}

export const FieldFilterTabs: React.FC<FieldFilterTabsProps> = ({
  field,
  type,
  mode,
  setMode,
  isSchemaField,
  initialFilter,
  label,
  isLoadingOptions,
  calcOptions,
  value,
  totalRowCount,
  emptyState,
  setSearch,
  onChange,
  onFilterChange,
  filterObject,
  conditionValue,
  operator,
  isMultipleCondition,
  disablePortalInOperatorSelect,
  handleSelectConditionChange,
  setErrors,
  setConditionValue,
  errors,
  activeProjName,
  advancedFilterValue,
  handleAdvancedFilterChange,
  setOpen,
  getErrors,
  setAdvancedFilterValue,
  open = false,
}) => {
  const { showAdvanced } = useFilterConfig();

  const viewOptions = useMemo<Array<{ title: string; value: FieldFilterModeType }>>(() => {
    const opts: Array<{ title: string; value: FieldFilterModeType }> = [
      { title: 'List', value: FieldFilterMode.LIST },
      { title: 'Condition', value: FieldFilterMode.CONDITION },
    ];
    if (showAdvanced) {
      opts.push({ title: 'Advanced', value: FieldFilterMode.ADVANCED });
    }
    return opts;
  }, [showAdvanced]);

  const { isNumberField, isBooleanField, isTextField } = getFieldTypeInfo(type);

  return (
    <>
      {initialFilter && !isNumberField && (
        <Box className="px-4 py-2">
          <RadioButton options={viewOptions} value={mode} onChange={value => setMode(value as FieldFilterModeType)} />
        </Box>
      )}
      <Divider />

      {(isTextField || isBooleanField) && isListFilterMode(mode) && (
        <FilterByList
          label={label}
          isLoadingOptions={isLoadingOptions}
          options={calcOptions}
          value={filterObject ? [] : isBooleanField ? value?.map(v => JSON.parse(v as string)) : value}
          onChange={onChange}
          field={field}
          type={type}
          onFilterChange={onFilterChange}
          totalOptionsCount={totalRowCount ?? 0}
          emptyState={emptyState}
          isSchemaField={isSchemaField}
          setSearch={setSearch}
          open={open}
        />
      )}

      {(isConditionFilterMode(mode) || !isTextField) && (
        <FilterByCondition
          initialFilter={initialFilter}
          conditionValue={conditionValue}
          operator={operator}
          isMultipleCondition={isMultipleCondition}
          disablePortalInOperatorSelect={disablePortalInOperatorSelect}
          type={type}
          handleSelectConditionChange={handleSelectConditionChange}
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          errors={errors}
        />
      )}

      {isAdvancedFilterMode(mode) && (
        <FilterByAdvanced activeProjName={activeProjName} advancedFilter={advancedFilterValue} onChange={handleAdvancedFilterChange} />
      )}

      <Divider orientation="horizontal" />

      {(isConditionFilterMode(mode) || isAdvancedFilterMode(mode) || isNumberField) && (
        <FilterActionButtons
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          operator={operator}
          initialFilter={initialFilter}
          setOpen={setOpen}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          conditionValue={conditionValue}
          getErrors={getErrors}
          mode={mode}
          advancedFilterValue={advancedFilterValue}
          setAdvancedFilterValue={setAdvancedFilterValue}
        />
      )}
    </>
  );
};
